import { ViewAlphaQuoteResponse } from '@snap-types/actions/view-alpha-quote';

import { FALSE_VALUE_STRING, TRUE_VALUE_STRING } from '../../constants';
import { buildLineItem } from '../builders/line-item';
import { buildQuantityBreak } from '../builders/quantity-break';
import { buildSelectChoice } from '../builders/select-choice';
import {
  buildBooleanValue,
  buildDecimalValue,
  buildDescriptionValue,
  buildNumberValue,
  buildSelectValue,
  buildTextValue,
} from '../builders/value';

function buildOverrideFlagValue(isOverridden: boolean) {
  return buildBooleanValue(
    isOverridden,
    isOverridden ? TRUE_VALUE_STRING : FALSE_VALUE_STRING,
  );
}

export function buildImportedLineState(
  importedQuoteLine: ViewAlphaQuoteResponse['quoteLines'][number],
  lineNumber: number,
) {
  const lineItem = buildLineItem({
    uuid: importedQuoteLine.uuid,
    dimensionUnit: importedQuoteLine.calculatedData?.dimensionUnit,
    status: importedQuoteLine.status,
    evaluationStatus: importedQuoteLine.evaluationStatus,
    syncStatus: importedQuoteLine.syncStatus,
    snapQuoteLineUuid: importedQuoteLine.snapQuoteLineUuid,
    lastAppliedAt: importedQuoteLine.lastAppliedAt,
    lastApplyFailedAt: importedQuoteLine.lastApplyFailedAt,
    lineNumber: buildNumberValue(lineNumber),

    description: buildDescriptionValue(importedQuoteLine.description),
    descriptionIsOverridden: buildBooleanValue(
      importedQuoteLine.descriptionIsOverridden,
    ),

    drawingId: buildTextValue(importedQuoteLine.drawingId),
    drawingRevisionNo: buildTextValue(importedQuoteLine.drawingRevisionNo),
    notes: buildTextValue(importedQuoteLine.notes),
    customerPartId: buildTextValue(importedQuoteLine.customerPartId),
    fastenerType: buildSelectValue(
      buildSelectChoice(importedQuoteLine.fastenerType),
    ),
    material: buildSelectValue(buildSelectChoice(importedQuoteLine.material)),
    dfars: buildBooleanValue(importedQuoteLine.dfars),
    threadSize: buildSelectValue(
      buildSelectChoice(importedQuoteLine.threadSize),
    ),
    threadPitch: buildSelectValue(
      buildSelectChoice(importedQuoteLine.threadPitch?.toString() ?? null),
    ),
    length: buildDecimalValue(importedQuoteLine.length),
    shoulderDiameter: buildDecimalValue(importedQuoteLine.shoulderDiameter),
    shoulderLength: buildDecimalValue(importedQuoteLine.shoulderLength),
    headStyle: buildSelectValue(buildSelectChoice(importedQuoteLine.headStyle)),
    recess: buildSelectValue(buildSelectChoice(importedQuoteLine.recess)),
    serration: buildBooleanValue(importedQuoteLine.serration),
    surfaceFinish: buildSelectValue(
      buildSelectChoice(importedQuoteLine.surfaceFinish),
    ),
    postFinish: buildSelectValue(
      buildSelectChoice(importedQuoteLine.postFinish),
    ),
    sems1Style: buildSelectValue(
      importedQuoteLine.sems1Style
        ? buildSelectChoice(importedQuoteLine.sems1Style)
        : null,
    ),
    sems1Weight: buildDecimalValue(importedQuoteLine.sems1Weight),
    sems2Style: buildSelectValue(
      importedQuoteLine.sems2Style
        ? buildSelectChoice(importedQuoteLine.sems2Style)
        : null,
    ),
    sems2Weight: buildDecimalValue(importedQuoteLine.sems2Weight),
    osp2: buildSelectValue(
      importedQuoteLine.osp2 ? buildSelectChoice(importedQuoteLine.osp2) : null,
    ),
    osp2Description: buildTextValue(importedQuoteLine.osp2Description),
    osp2UOM: buildSelectValue(
      importedQuoteLine.osp2UOM
        ? buildSelectChoice(importedQuoteLine.osp2UOM)
        : null,
    ),
    osp2MinLot: buildDecimalValue(importedQuoteLine.osp2MinLot),
    osp3: buildSelectValue(
      importedQuoteLine.osp3 ? buildSelectChoice(importedQuoteLine.osp3) : null,
    ),
    osp3Description: buildTextValue(importedQuoteLine.osp3Description),
    osp3UOM: buildSelectValue(
      importedQuoteLine.osp3UOM
        ? buildSelectChoice(importedQuoteLine.osp3UOM)
        : null,
    ),
    osp3MinLot: buildDecimalValue(importedQuoteLine.osp3MinLot),

    toolingNote: buildTextValue(importedQuoteLine.toolingNote),
    headingNote: buildTextValue(importedQuoteLine.headingNote),
    trimmingNote: buildTextValue(importedQuoteLine.trimmingNote),
    pinchNote: buildTextValue(importedQuoteLine.pinchNote),
    rollingNote: buildTextValue(importedQuoteLine.rollingNote),
    shankSlottingNote: buildTextValue(importedQuoteLine.shankSlottingNote),
    sawSlottingNote: buildTextValue(importedQuoteLine.sawSlottingNote),
    dephosNote: buildTextValue(importedQuoteLine.dephosNote),
    heatTreatNote: buildTextValue(importedQuoteLine.heatTreatNote),
    surfaceFinishNote: buildTextValue(importedQuoteLine.surfaceFinishNote),
    postFinishNote: buildTextValue(importedQuoteLine.postFinishNote),
    osp2Note: buildTextValue(importedQuoteLine.osp2Note),
    osp3Note: buildTextValue(importedQuoteLine.osp3Note),

    calculatedData: importedQuoteLine.calculatedData,
    attachments: importedQuoteLine.attachments ?? [],

    hasSemsEmbed: buildBooleanValue(importedQuoteLine.hasSemsEmbed),
  });

  const quantityBreaks = importedQuoteLine.quotePrices.map(
    (importedQuotePrice) => {
      const quantityBreak = buildQuantityBreak({
        uuid: importedQuotePrice.uuid,
        lineItemUuid: importedQuoteLine.uuid,
        evaluationStatus: importedQuotePrice.evaluationStatus,
        quantity: buildDecimalValue(importedQuotePrice.quantity),
        sems1Cost: buildDecimalValue(importedQuotePrice.sems1Cost),
        sems2Cost: buildDecimalValue(importedQuotePrice.sems2Cost),
        osp2Price: buildDecimalValue(importedQuotePrice.osp2Price),
        osp3Price: buildDecimalValue(importedQuotePrice.osp3Price),

        userGrossMargin: buildDecimalValue(importedQuotePrice.userGrossMargin),
        grossMarginIsOverridden: buildOverrideFlagValue(
          importedQuotePrice.grossMarginIsOverridden,
        ),
        userUnitPrice: buildDecimalValue(importedQuotePrice.userUnitPrice),
        unitPriceIsOverridden: buildOverrideFlagValue(
          importedQuotePrice.unitPriceIsOverridden,
        ),
        userLeadTimeWeeks: buildNumberValue(
          importedQuotePrice.userLeadTimeWeeks,
        ),
        leadTimeWeeksIsOverridden: buildOverrideFlagValue(
          importedQuotePrice.leadTimeWeeksIsOverridden,
        ),

        calculatedData: importedQuotePrice.calculatedData,
      });

      lineItem.quantityBreakUuids.push(quantityBreak.uuid);

      return quantityBreak;
    },
  );

  if (quantityBreaks.length === 0) {
    const quantityBreak = buildQuantityBreak({
      lineItemUuid: importedQuoteLine.uuid,
      quantity: buildDecimalValue(null),
      sems1Cost: buildDecimalValue(null),
      sems2Cost: buildDecimalValue(null),
      osp2Price: buildDecimalValue(null),
      osp3Price: buildDecimalValue(null),
    });

    lineItem.quantityBreakUuids.push(quantityBreak.uuid);
    quantityBreaks.push(quantityBreak);
  }

  return {
    lineItem,
    quantityBreaks,
  };
}
